/* eslint-disable max-lines */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useVirtualizer } from '@tanstack/react-virtual';
import { generateUniqueId, normalizeText } from '@services/utils.service';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import {
  Button, Icon, Input, Tooltip, 
} from '@nazka/nazka.mapframe.components';

import Chips from '@App/components/Chips/Chips';
import MobilityIcon from '@App/components/MobilityIcon/MobilityIcon';
import LegendControl from '@App/components/Map/controls/Legend/Legend';

import {
  difference, polygon, featureCollection, bbox,
} from '@turf/turf';

import { setVacancy } from '@App/pages/JobVacancy/job-vacancy.slice';
import { getJobsFromVacancies, getClusterIds } from '@App/pages/JobVacancies/job-vacancies.utils';

import theme from '@css/theme.style.json';

import './job-vacancies.styl';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { mobilityOfferExists } from '../Admin/admin.utils';

function JobVacancies() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    clickedFeature,
    hoveredFeature,
    map,
    scrollPositionRef,
    searchValue,
    setSearchValue,
  } = useOutletContext();
  const parentRef = useRef();
  const prevHighlightedJob = useRef();
  const [columnsCount, setColumnsCount] = useState(2);
  const [highlightedJob, setHighlightedJob] = useState(2);

  const homeAddress = useSelector(state => state.map.homeAddress);
  const vacancies = useSelector(state => state.map.vacancies);
  const searchGeometry = useSelector(state => state.map.searchGeometry);

  const jobs = useMemo(() => {
    const jobsFromVacancies = getJobsFromVacancies(vacancies);

    if (searchValue) {
      return jobsFromVacancies.filter((v) => {
        return v?.properties?.functie?.functieTitel?.toLowerCase().includes(searchValue.toLowerCase())
          || v?.properties?.company?.NAZKA_NM?.toLowerCase().includes(searchValue.toLowerCase());
      });
    }

    return jobsFromVacancies.sort((a, b) => {
      return (a?.properties?.company?.isJobAggregator === true)
        - (b?.properties?.company?.isJobAggregator === true);
    });
  }, [vacancies, searchValue]);

  const highlightedJobs = useMemo(() => {
    let clusterFeatureIds = [];
    if (hoveredFeature?.properties?.cluster) {
      clusterFeatureIds = getClusterIds(hoveredFeature);
    } else {
      let vacatureReferenties = hoveredFeature?.properties?.vacancies || [];
      vacatureReferenties = typeof vacatureReferenties === 'string' ? JSON.parse(vacatureReferenties) : vacatureReferenties;
      clusterFeatureIds = vacatureReferenties.map(v => ({
        OIDN: String(hoveredFeature?.properties?.OIDN),
        vdabReferentie: String(v?.properties?.vacatureReferentie?.vdabReferentie),
      }));
    }

    return jobs.filter((job) => {
      return clusterFeatureIds.find(id => (
        String(job?.properties?.vacatureReferentie?.vdabReferentie) === id?.vdabReferentie
        && String(job?.properties?.company?.OIDN) === id?.OIDN
      ));
    });
  }, [hoveredFeature, jobs]);

  const {
    jobsWithAddressCount,
    jobsWithNoAddressCount,
    jobsFromAggregatorCount,
  } = useMemo(() => (
    jobs.reduce((acc, job) => {
      const isAggregator = job?.properties?.company?.isJobAggregator;
      const hasDistance = job?.properties?.company?.distanceToHomeAddress;

      if (isAggregator) acc.jobsFromAggregatorCount += 1;
      else if (hasDistance) acc.jobsWithAddressCount += 1;
      else acc.jobsWithNoAddressCount += 1;

      return acc;
    }, {
      jobsWithAddressCount: 0,
      jobsWithNoAddressCount: 0,
      jobsFromAggregatorCount: 0,
    })
  ), [jobs]);

  const [
    addressRowCount,
    noAddressRowCount,
    jobAggregatorRowCount,
  ] = useMemo(() => {
    return [
      Math.ceil(jobsWithAddressCount / columnsCount),
      Math.ceil(jobsWithNoAddressCount / columnsCount),
      Math.ceil(jobsFromAggregatorCount / columnsCount),
    ];
  }, [columnsCount, jobsWithAddressCount, jobsFromAggregatorCount, jobsWithNoAddressCount]);

  const rowVirtualizer = useVirtualizer({
    count: addressRowCount + noAddressRowCount + jobAggregatorRowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: index => (parseInt(theme.layout.spacing, 10) * 34)
      + (index === addressRowCount || index === addressRowCount + noAddressRowCount ? 48 : 0),
    overscan: 5,
    gap: parseInt(theme.layout.spacing, 10) * 3,
  });

  useEffect(() => {
    if (!homeAddress) navigate('/');
  }, [homeAddress, navigate]);

  useEffect(() => {
    if (!columnsCount || !clickedFeature?.properties?.vacancies) return;

    let { vacancies: companyVacancies } = clickedFeature.properties;
    companyVacancies = typeof companyVacancies === 'string' ? JSON.parse(companyVacancies) : companyVacancies;
    companyVacancies = companyVacancies.map(v => String(v?.properties?.vacatureReferentie?.vdabReferentie));

    const indexes = jobs.map((job, index) => {
      const contains = companyVacancies.find(id => (
        String(job?.properties?.vacatureReferentie?.vdabReferentie) === id
        && String(job?.properties?.company?.OIDN) === clickedFeature?.properties?.OIDN
      ));
      return contains ? index : -1;
    }).filter(index => index !== -1);

    const index = Math.floor(indexes[0] / columnsCount);

    if (indexes.length) {
      rowVirtualizer.scrollToIndex(
        index,
        { align: 'start', behavior: jobs.length / columnsCount > 50 ? 'auto' : 'smooth' },
      );
    }
  }, [jobs, clickedFeature, rowVirtualizer, columnsCount, jobsWithNoAddressCount]);

  useEffect(() => {
    const parent = parentRef.current;
    const observer = new ResizeObserver((entries) => {
      const containerWidth = entries[0].contentRect.width;
      const cardWidth = 362; // Width from your CSS
      const gap = 24; // Gap from your CSS
      const padding = 64; // Left and right padding combined
      const availableWidth = containerWidth - padding;
      const columns = Math.floor((availableWidth + gap) / (cardWidth + gap));
      setColumnsCount(Math.max(1, columns));
    });

    if (parent) {
      observer.observe(parent);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const parent = parentRef.current;
    parent.scrollTop = parseInt(scrollPositionRef.current, 10);

    const handleScroll = () => {
      scrollPositionRef.current = parent.scrollTop;
    };

    parent.addEventListener('scroll', handleScroll);

    return () => {
      parent.removeEventListener('scroll', handleScroll);
    };
  }, [scrollPositionRef]);

  useEffect(() => {
    const legendControl = new LegendControl();
    if (map) {
      if (map.getSource('vacancy-route')) {
        map.getSource('vacancy-route').setData({ type: 'FeatureCollection', features: [] });
      }
      map.addControl(legendControl, 'bottom-right');
    }

    return () => {
      if (map) {
        map.removeControl(legendControl);
      }
    }
  }, [map]);

  useEffect(() => {
    if (map && searchGeometry && map.getSource('search-geometry')) {
      const worldBounds = polygon([[[-180, -90], [180, -90], [180, 90], [-180, 90], [-180, -90]]]);
      const searchGeometryNegative = difference(featureCollection([worldBounds, searchGeometry]));
      map.getSource('search-geometry').setData(searchGeometryNegative);
      map.fitBounds(bbox(searchGeometry), {
        padding: 20, maxZoom: 15,
      });
    }

    return () => map && map.getSource('search-geometry').setData({
      type: 'FeatureCollection',
      features: [],
    });
  }, [map, searchGeometry]);

  useEffect(() => {
    if (map && highlightedJob) {
      let foundCluster = false;
      const clusters = map.queryRenderedFeatures({
        layers: ['vacancies-clusters'],
      });

      clusters.forEach((cluster) => {
        const clusterFeatureIds = getClusterIds(cluster);

        if (clusterFeatureIds
          && clusterFeatureIds.find(id => (
            String(highlightedJob?.vdabReferentie) === id?.vdabReferentie
            && String(highlightedJob?.OIDN) === id?.OIDN
          ))) {
          map.setFeatureState({ source: 'vacancies', id: cluster.id }, { highlight: true });
          prevHighlightedJob.current = cluster.id;
          foundCluster = true;
        }
      });

      if (!foundCluster) {
        map.setFeatureState({ source: 'vacancies', id: highlightedJob?.OIDN }, { highlight: true });
        prevHighlightedJob.current = highlightedJob?.OIDN;
      }
    }
    return () => {
      if (prevHighlightedJob.current) {
        map.setFeatureState({ source: 'vacancies', id: prevHighlightedJob.current }, { highlight: false });
      }
    }
  }, [map, highlightedJob]);

  return (
    <div className="job-vacancies">
      <Chips />
      <div className="job-vacancies__header">
        {(!!jobs.length || !!searchValue.length) && (
          <div className="job-vacancies__header__search">
            <Icon icon={faSearch} className="job-vacancies__header__search__icon" />
            <Input
              className="job-vacancies__header__search__input"
              placeholder="Zoek..."
              value={searchValue}
              onInputChange={c => setSearchValue(c.target.value)}
            />
          </div>
        )}
        <span>
          {`${jobs.length} vacature${jobs.length === 1 ? '' : 's'} `}
          {`in ${vacancies.length} bedrij${vacancies.length === 1 ? 'f' : 'ven'}`}
        </span>
      </div>
      <div
        ref={parentRef}
        className="job-vacancies__list"
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          let startIndex = virtualRow.index * columnsCount;

          let section = 'address';
          let relativeIndex = virtualRow.index;

          if (virtualRow.index >= addressRowCount + noAddressRowCount) {
            section = 'aggregator';
            relativeIndex = virtualRow.index - addressRowCount - noAddressRowCount;
          } else if (virtualRow.index >= addressRowCount) {
            section = 'noAddress';
            relativeIndex = virtualRow.index - addressRowCount;
          }

          startIndex = relativeIndex * columnsCount;

          let rowVacancies = [];
          if (section === 'address') {
            rowVacancies = jobs
              .filter(j => !j?.properties?.company?.isJobAggregator && j?.properties?.company?.distanceToHomeAddress)
              .slice(startIndex, startIndex + columnsCount);
          } else if (section === 'noAddress') {
            rowVacancies = jobs
              .filter(j => !j?.properties?.company?.isJobAggregator && !j?.properties?.company?.distanceToHomeAddress)
              .slice(startIndex, startIndex + columnsCount);
          } else if (section === 'aggregator') {
            rowVacancies = jobs
              .filter(j => j?.properties?.company?.isJobAggregator)
              .slice(startIndex, startIndex + columnsCount);
          }

          return (
            <div
              key={virtualRow.index}
              className="job-vacancies__list__row"
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {!!jobsWithNoAddressCount && virtualRow.index === addressRowCount && (
                <div className="job-vacancies__list__row__no-address-seperator">
                  <span>
                    {`${jobsWithNoAddressCount} vacature${jobsWithNoAddressCount > 1 ? 's' : ''} in deze regio met een onbekend adres`}
                  </span>
                </div>
              )}
              {!!jobsFromAggregatorCount && virtualRow.index === addressRowCount + noAddressRowCount && (
                <div className="job-vacancies__list__row__no-address-seperator">
                  <span>
                    {`${jobsFromAggregatorCount} vacature${jobsFromAggregatorCount > 1 ? 's' : ''} bij jobkantoren, deze jobs kunnen ook elders in het land zijn`}
                  </span>
                </div>
              )}
              <div className="job-vacancies__list__row__items">
                {rowVacancies.map((vacancy) => {
                  const {
                    vacatureReferentie: { vdabReferentie },
                    arbeidsovereenkomst: {
                      arbeidsRegime,
                      arbeidsStelsel,
                    },
                    functie: { functieTitel },
                    company: {
                      OIDN, NAZKA_NM, distanceToHomeAddress, mobilityOffer, isJobAggregator,
                    },
                  } = vacancy.properties || {};

                  return (
                    <Button
                      key={vdabReferentie}
                      className="job-vacancies__list__row__items__item"
                      onClick={() => {
                        dispatch(setVacancy({ vdabReferentie, OIDN }));
                        navigate(`/${OIDN}/${vdabReferentie}`);

                        window.gtag?.('event', 'vacancies_vacancy_select', {
                          event_category: 'Vacancies',
                          event_label: 'Selected a vacancy',
                          OIDN,
                          vdabReferentie,
                        });
                      }}
                      rootElProps={{
                        onMouseEnter: () => setHighlightedJob({ vdabReferentie, OIDN }),
                        onMouseLeave: () => setHighlightedJob(null),
                        'data-highlight': !!highlightedJobs.find((job) => {
                          return (
                            job?.properties?.vacatureReferentie?.vdabReferentie === vdabReferentie
                            && job?.properties?.company?.OIDN === OIDN
                          )
                        }),
                      }}
                      hover={false}
                      layout="ghost"
                    >
                      <div className="job-vacancies__list__row__items__item__header">
                        <h4 
                          className="job-vacancies__list__row__items__item__header__title"
                          title={normalizeText(functieTitel)}
                        >
                          {normalizeText(functieTitel)}
                        </h4>
                        <p className="job-vacancies__list__row__items__item__header__info">
                          {`${arbeidsRegime?.label}`}
                          {arbeidsStelsel && arbeidsStelsel.map(({ label }) => {
                            return (`, ${label}`)
                          })}
                        </p>
                      </div>
                      <div className="job-vacancies__list__row__items__item__footer">
                        <div className="job-vacancies__list__row__items__item__footer__company">
                          {mobilityOfferExists(mobilityOffer) && (
                            <MobilityIcon className="job-vacancies__list__row__items__item__footer__company__icon" />
                          )}
                          <span 
                            className="job-vacancies__list__row__items__item__footer__company__text"
                            title={NAZKA_NM}
                          >
                            {NAZKA_NM}
                          </span>
                        </div>
                        <div className="job-vacancies__list__row__items__item__footer__distance">
                          {distanceToHomeAddress && !isJobAggregator ? `${new Intl.NumberFormat('fr-BE', {
                            maximumFractionDigits: 2,
                          }).format(distanceToHomeAddress)} km` : ''}
                          {!distanceToHomeAddress && !isJobAggregator && (
                            <Tooltip
                              id={`${OIDN}-${vdabReferentie}`}
                              clickable
                              tooltip={(
                                <span className="job-vacancies__list__row__items__item__footer__tooltip">
                                  We weten dat dit een job in de regio is, maar we konden niet vinden exact waar.
                                </span>
                              )}
                            >
                              <Icon
                                className="job-vacancies__list__row__items__item__footer__tooltip__icon"
                                icon={faQuestionCircle}
                                size="16px"
                                rootElProps={{
                                  'data-tooltip-id': `${OIDN}-${vdabReferentie}`,
                                }}
                              />
                            </Tooltip>
                          )}
                          {isJobAggregator && (
                            <Tooltip
                              id={`${OIDN}-${vdabReferentie}`}
                              clickable
                              tooltip={(
                                <span className="job-vacancies__list__row__items__item__footer__tooltip">
                                  {distanceToHomeAddress && 'We weten niet waar deze job is. Daarom tonen we je het jobkantoor dat deze job aanbiedt.'}
                                  {!distanceToHomeAddress && 'Deze job wordt aangeboden door een jobkantoor. We weten dat dit een job in de regio is, maar konden niet vinden exact waar.'}
                                </span>
                              )}
                            >
                              <Icon
                                className="job-vacancies__list__row__items__item__footer__tooltip__icon"
                                icon={distanceToHomeAddress ? '/img/icons/intrim.svg' : '/img/icons/intrim-no-address.svg'}
                                size="18px"
                                rootElProps={{
                                  'data-tooltip-id': `${OIDN}-${vdabReferentie}`,
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </Button>
                  );
                })}
                {Array.from({ length: columnsCount - rowVacancies.length }).map(() => (
                  <div className="job-vacancies__list__row__items__filler" key={generateUniqueId()} />
                ))}
              </div>
            </div>
          );
        })}
        <div
          className="job-vacancies__list__placeholder"
          style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
        />
      </div>
      {jobs.length > 10 && (
        <div className="job-vacancies__footer-info">
          {!!jobsWithNoAddressCount && (
            <Button
              className="job-vacancies__footer-info__button"
              onClick={() => {
                rowVirtualizer.scrollToIndex(addressRowCount, { align: 'start' });
                window.gtag?.('event', 'vacancies_click_scroll_to_vacancies_with_no_address_button', {
                  event_category: 'Vacancies',
                  event_label: 'Click scroll to vacancies with no address button',
                });
              }}
            >
              {`${jobsWithNoAddressCount} vacature${jobsWithNoAddressCount > 1 ? 's' : ''} met een onbekend adres`}
            </Button>
          )}
          {!!jobsFromAggregatorCount && (
            <Button
              className="job-vacancies__footer-info__button"
              onClick={() => {
                rowVirtualizer.scrollToIndex(addressRowCount + noAddressRowCount, { align: 'start' });
                window.gtag?.('event', 'vacancies_click_scroll_to_vacancies_from_aggregator_button', {
                  event_category: 'Vacancies',
                  event_label: 'Click scroll to vacancies from aggregator',
                });
              }}
            >
              {`${jobsFromAggregatorCount} vacature${jobsFromAggregatorCount > 1 ? 's' : ''} bij jobkantoren`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default JobVacancies;